/**
 * Bid Comparison Utility Functions
 *
 * Pure functions for scoring, anomaly detection, normalization,
 * and data transformation used by BidComparisonPage.
 */

// ── Constants ────────────────────────────────────────────────────────────
export const ANOMALY_THRESHOLD = 0.80; // Flag bids 20%+ below average

export const DEFAULT_WEIGHTS = {
  price: 40,
  timeline: 20,
  scopeCoverage: 20,
  warranty: 10,
  deposit: 10,
};

export const WEIGHT_LABELS = {
  price: 'Price',
  timeline: 'Timeline',
  scopeCoverage: 'Scope Coverage',
  warranty: 'Warranty',
  deposit: 'Deposit',
};

// ── Data Helpers ─────────────────────────────────────────────────────────

/** Safely parse JSONB arrays that may be string, array, or null */
export const ensureArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

/** Parse cost_breakdown into normalized array of { category, description, amount } */
export const parseCostBreakdown = (costBreakdown) => {
  const arr = ensureArray(costBreakdown);
  return arr.map(item => ({
    category: item.category || item.name || 'Other',
    description: item.description || '',
    amount: parseFloat(item.amount || item.cost || 0),
  }));
};

/** Extract warranty months from various formats ("12 months", "1 year", "24") */
export const parseWarrantyMonths = (warranty) => {
  if (!warranty) return 0;
  const str = String(warranty).toLowerCase();
  const yearMatch = str.match(/(\d+)\s*year/);
  if (yearMatch) return parseInt(yearMatch[1]) * 12;
  const monthMatch = str.match(/(\d+)\s*month/);
  if (monthMatch) return parseInt(monthMatch[1]);
  const numMatch = str.match(/(\d+)/);
  if (numMatch) return parseInt(numMatch[1]);
  return 0;
};

/** Normalize a scope item string for deduplication */
const normalizeItem = (item) => {
  if (typeof item === 'string') return item.toLowerCase().trim();
  if (typeof item === 'object' && item !== null) {
    return (item.item || item.name || item.description || JSON.stringify(item)).toLowerCase().trim();
  }
  return String(item).toLowerCase().trim();
};

// ── Price Analysis ──────────────────────────────────────────────────────

export const calculatePriceStats = (bids) => {
  const prices = bids.map(b => parseFloat(b.total_cost || 0)).filter(p => p > 0);
  if (prices.length === 0) return { min: 0, max: 0, average: 0, spread: 0, spreadPercent: 0 };

  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const average = prices.reduce((s, p) => s + p, 0) / prices.length;
  const spread = max - min;
  const spreadPercent = average > 0 ? ((spread / average) * 100) : 0;

  return { min, max, average, spread, spreadPercent };
};

export const isAnomalyBid = (bidTotal, averageTotal) => {
  if (averageTotal <= 0 || bidTotal <= 0) return false;
  return bidTotal < averageTotal * ANOMALY_THRESHOLD;
};

export const getAnomalyDeviation = (bidTotal, averageTotal) => {
  if (averageTotal <= 0) return 0;
  return ((averageTotal - bidTotal) / averageTotal) * 100;
};

// ── Cost Breakdown Comparison ───────────────────────────────────────────

/** Get all unique cost categories across all bids */
export const getAllCostCategories = (bids) => {
  const categories = new Set();
  bids.forEach(bid => {
    const breakdown = parseCostBreakdown(bid.cost_breakdown);
    breakdown.forEach(item => categories.add(item.category));
  });
  return Array.from(categories).sort();
};

/** Get lowest amount for a given category across all bids */
export const getLowestForCategory = (bids, category) => {
  let lowest = Infinity;
  bids.forEach(bid => {
    const breakdown = parseCostBreakdown(bid.cost_breakdown);
    const item = breakdown.find(i => i.category === category);
    if (item && item.amount > 0 && item.amount < lowest) {
      lowest = item.amount;
    }
  });
  return lowest === Infinity ? 0 : lowest;
};

/** Get bid's amount for a category */
export const getBidCategoryAmount = (bid, category) => {
  const breakdown = parseCostBreakdown(bid.cost_breakdown);
  const item = breakdown.find(i => i.category === category);
  return item ? item.amount : null;
};

// ── Scope Coverage Matrix ───────────────────────────────────────────────

/**
 * Build scope matrix: { items: string[], matrix: { [bidId]: { [item]: 'included'|'excluded'|'not_mentioned' } }, riskItems: string[] }
 */
export const buildScopeMatrix = (bids) => {
  const allItems = new Set();
  const bidMaps = {};

  bids.forEach(bid => {
    const inclusions = ensureArray(bid.inclusions).map(normalizeItem);
    const exclusions = ensureArray(bid.exclusions).map(normalizeItem);

    inclusions.forEach(i => allItems.add(i));
    exclusions.forEach(i => allItems.add(i));

    const map = {};
    inclusions.forEach(i => { map[i] = 'included'; });
    exclusions.forEach(i => { map[i] = 'excluded'; });
    bidMaps[bid.id] = map;
  });

  const items = Array.from(allItems).sort();

  // Identify risk items: where one bid excludes and another includes
  const riskItems = items.filter(item => {
    const statuses = bids.map(bid => bidMaps[bid.id]?.[item] || 'not_mentioned');
    return statuses.includes('included') && statuses.includes('excluded');
  });

  return { items, bidMaps, riskItems };
};

export const getScopeStatus = (bidMaps, bidId, item) => {
  return bidMaps[bidId]?.[item] || 'not_mentioned';
};

export const getScopeCoverageCount = (bid) => {
  return ensureArray(bid.inclusions).length;
};

// ── Weighted Scoring ────────────────────────────────────────────────────

/**
 * Normalize a value to 0-100 scale.
 * lowerIsBetter: true for price, duration, deposit (lower = better = higher score)
 */
// Bid durations are stored as a number + unit (days/weeks/months). Normalize to
// days before comparing or ranking — raw numbers across units are meaningless.
const DURATION_UNIT_DAYS = { days: 1, weeks: 7, months: 30 };

export const durationInDays = (bid) => {
  const n = parseInt(bid?.estimated_duration, 10);
  if (!n || n <= 0) return 0;
  return n * (DURATION_UNIT_DAYS[bid?.duration_unit] || 7);
};

export const formatDuration = (bid) => {
  const n = parseInt(bid?.estimated_duration, 10);
  return n ? `${n} ${bid?.duration_unit || 'weeks'}` : '—';
};

const normalizeScore = (value, min, max, lowerIsBetter = false) => {
  if (max === min) return 100; // All same = all perfect
  const range = max - min;
  if (lowerIsBetter) {
    return 100 * (1 - (value - min) / range);
  }
  return 100 * (value - min) / range;
};

/**
 * Calculate weighted composite scores for all bids.
 * Returns: { [bidId]: { price, timeline, scopeCoverage, warranty, deposit, composite } }
 */
export const calculateScores = (bids, weights = DEFAULT_WEIGHTS) => {
  if (bids.length === 0) return {};

  // Extract raw values
  const rawValues = bids.map(bid => ({
    id: bid.id,
    price: parseFloat(bid.total_cost || 0),
    timeline: durationInDays(bid),
    scopeCoverage: getScopeCoverageCount(bid),
    warranty: parseWarrantyMonths(bid.warranty_period),
    deposit: parseFloat(bid.deposit_amount || 0),
  }));

  // Find min/max for each criterion
  const criteria = ['price', 'timeline', 'scopeCoverage', 'warranty', 'deposit'];
  const lowerIsBetter = { price: true, timeline: true, scopeCoverage: false, warranty: false, deposit: true };
  const minMax = {};

  criteria.forEach(c => {
    const values = rawValues.map(v => v[c]).filter(v => v > 0);
    minMax[c] = {
      min: values.length > 0 ? Math.min(...values) : 0,
      max: values.length > 0 ? Math.max(...values) : 0,
    };
  });

  // Calculate normalized + composite scores
  const scores = {};
  const totalWeight = Object.values(weights).reduce((s, w) => s + w, 0);

  rawValues.forEach(raw => {
    const normalized = {};
    let compositeSum = 0;

    criteria.forEach(c => {
      const val = raw[c];
      if (val === 0 && minMax[c].max === 0) {
        normalized[c] = 50; // No data = neutral
      } else {
        normalized[c] = normalizeScore(val, minMax[c].min, minMax[c].max, lowerIsBetter[c]);
      }
      compositeSum += normalized[c] * (weights[c] || 0);
    });

    scores[raw.id] = {
      ...normalized,
      composite: totalWeight > 0 ? compositeSum / totalWeight : 0,
    };
  });

  return scores;
};

/** Get the bid ID with the highest composite score */
export const getTopScoredBid = (scores) => {
  let topId = null;
  let topScore = -1;
  Object.entries(scores).forEach(([id, s]) => {
    if (s.composite > topScore) {
      topScore = s.composite;
      topId = id;
    }
  });
  return topId;
};

// ── Currency ────────────────────────────────────────────────────────────

export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

export const hasMixedCurrencies = (bids) => {
  const currencies = new Set(bids.map(b => (b.currency || 'USD').toUpperCase()));
  return currencies.size > 1;
};
